// src/contexts/FontContext.js
//
// Font chooser — lets the user swap between Arabic fonts from the
// top bar. The selection is persisted in localStorage and applied by
// overriding the three global CSS variables the theme reads.
//
// NOTE: adding a new option is a one-line change to FONT_OPTIONS.
// The 4 "quran*" entries are dedicated Quran typesetting faces with
// full tashkeel/harakat and Quranic marks (small alef, hamzat wasl,
// pause marks, sajdah, etc.). All are loaded from Google Fonts in
// public/index.html.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'bayan-font';

const DEFAULT_KEY = 'palestine';

/**
 * Each option controls three CSS variables + MUI theme font slots:
 *   - displayStack:  used for big Maghribi titles (hero, page headers)
 *   - bodyStack:     used for Arabic body text (paragraphs, lists)
 *   - labelStack:    optional override for "caption" labels; null falls
 *                    back to the current label font (Work Sans)
 */
export const FONT_OPTIONS = {
  palestine: {
    key: 'palestine',
    labelAr: 'خط بالستاين',
    labelEn: 'Palestine',
    description: 'خط فلسطيني — مألوف ومدرسي',
    displayStack: '"Palestine", "Aref Ruqaa", "Amiri", serif',
    bodyStack:    '"Palestine", "Noto Naskh Arabic", "Amiri", serif',
    labelStack:   null,
  },
  koMethlama: {
    key: 'koMethlama',
    labelAr: 'كو مذلمة',
    labelEn: 'Ko Methlama',
    description: 'خط مغربي مزخرف',
    displayStack: '"Ko Methlama", "Aref Ruqaa", "Amiri", serif',
    bodyStack:    '"Ko Methlama", "Noto Naskh Arabic", "Amiri", serif',
    labelStack:   null,
  },
  thuluth: {
    key: 'thuluth',
    labelAr: 'ثلث السمت',
    labelEn: 'Thuluth Alsmt 7017',
    description: 'خط الثلث — كلاسيكي وفخم',
    displayStack: '"Thuluth Alsmt7017", "Amiri Quran", "Aref Ruqaa", serif',
    bodyStack:    '"Thuluth Alsmt7017", "Amiri", "Noto Naskh Arabic", serif',
    labelStack:   null,
  },
  // ---- Dedicated Quran typesetting faces (full harakat + marks) ----
  amiriQuran: {
    key: 'amiriQuran',
    labelAr: 'أميري قرآن',
    labelEn: 'Amiri Quran',
    description: 'خط مُصمَّم خصيصا لرسم المصحف — حركات كاملة ورموز قرآنية',
    displayStack: '"Amiri Quran", "Amiri", "Scheherazade New", serif',
    bodyStack:    '"Amiri Quran", "Amiri", "Scheherazade New", serif',
    labelStack:   null,
  },
  scheherazade: {
    key: 'scheherazade',
    labelAr: 'شهرزاد',
    labelEn: 'Scheherazade New',
    description: 'خط نَسخي كلاسيكي من SIL — ممتاز لنصوص القرآن',
    displayStack: '"Scheherazade New", "Amiri", "Noto Naskh Arabic", serif',
    bodyStack:    '"Scheherazade New", "Amiri", "Noto Naskh Arabic", serif',
    labelStack:   null,
  },
  lateef: {
    key: 'lateef',
    labelAr: 'لطيف',
    labelEn: 'Lateef',
    description: 'خط نسخي رقيق وأنيق — مناسب للمطالعة الطويلة',
    displayStack: '"Lateef", "Scheherazade New", "Amiri", serif',
    bodyStack:    '"Lateef", "Scheherazade New", "Amiri", serif',
    labelStack:   null,
  },
  markazi: {
    key: 'markazi',
    labelAr: 'مركزي',
    labelEn: 'Markazi Text',
    description: 'خط نسخي حديث — واضح ومُشَكَّل بالكامل',
    displayStack: '"Markazi Text", "Noto Naskh Arabic", "Amiri", serif',
    bodyStack:    '"Markazi Text", "Noto Naskh Arabic", "Amiri", serif',
    labelStack:   null,
  },

  // Phase 5 (PLAN1.md §J.4) — Dyslexia-friendly option. The Latin side
  // uses OpenDyslexic (CDN-loaded so no asset bundle), and the Arabic
  // side stays on Noto Naskh since OpenDyslexic does not cover Arabic.
  // The key win for mixed-script UI is the Latin labels becoming
  // dyslexia-friendly without disrupting Arabic rendering.
  openDyslexic: {
    key: 'openDyslexic',
    labelAr: 'خط مناسب لعسر القراءة',
    labelEn: 'OpenDyslexic',
    description: 'خط مناسب لعسر القراءة — للأرقام والنصوص اللاتينية',
    displayStack: '"OpenDyslexic", "Aref Ruqaa", "Amiri", serif',
    bodyStack:    '"OpenDyslexic", "Noto Naskh Arabic", "Amiri", serif',
    labelStack:   '"OpenDyslexic", "Work Sans", sans-serif',
  },
};

const FontContext = createContext(null);

export const useFont = () => useContext(FontContext);

export const FontProvider = ({ children }) => {
  const [fontKey, setFontKey] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_KEY;
    const stored = localStorage.getItem(STORAGE_KEY);
    return FONT_OPTIONS[stored] ? stored : DEFAULT_KEY;
  });

  // Apply the selected stacks by writing CSS vars on <html>. The global
  // index.css reads these via var(--sacred-font-*).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const opt = FONT_OPTIONS[fontKey] || FONT_OPTIONS[DEFAULT_KEY];
    const root = document.documentElement;
    root.style.setProperty('--sacred-font-display', opt.displayStack);
    root.style.setProperty('--sacred-font-body', opt.bodyStack);
    // Label font intentionally tracks the body stack so every "tag/eyebrow"
    // text (KpiCard captions, table heads, section labels) reads in the
    // same naskh as the rest of the interface.
    root.style.setProperty('--sacred-font-label', opt.bodyStack);
    root.dataset.font = fontKey;
    try { localStorage.setItem(STORAGE_KEY, fontKey); } catch (_) {}
  }, [fontKey]);

  const setFont = useCallback((key) => {
    if (!FONT_OPTIONS[key]) return;
    setFontKey(key);
  }, []);

  const value = useMemo(() => ({
    fontKey,
    option: FONT_OPTIONS[fontKey] || FONT_OPTIONS[DEFAULT_KEY],
    options: Object.values(FONT_OPTIONS),
    setFont,
  }), [fontKey, setFont]);

  return <FontContext.Provider value={value}>{children}</FontContext.Provider>;
};
